import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Alert,
  Spinner,
} from "reactstrap";
import { Mail, ArrowRight, X } from "lucide-react";

const OTPVerityLogin = ({
  isOpen,
  toggle,
  mobile,
  onVerifyOTP,
  onResendOTP,
  loginResponse,
  isLoading,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [localLoading, setLocalLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState("");
  const otpRefs = useRef([]);

  // Initialize refs for OTP inputs
  useEffect(() => {
    otpRefs.current = otpRefs.current.slice(0, 6);
  }, []);

  // Timer for resend OTP
  useEffect(() => {
    let interval;
    if (isOpen && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer, isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Auto-focus first input
      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 100);
      setTimer(30);
      setCanResend(false);
      setError("");
      setOtp(["", "", "", "", "", ""]);
    }
  }, [isOpen]);

  const resetState = () => {
    setOtp(["", "", "", "", "", ""]);
    setLocalLoading(false);
    setTimer(30);
    setCanResend(false);
    setError("");
  };

  const handleOtpChange = (index, value) => {
    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtp(newOtp);
    setError(""); // Clear error when user types

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];

      if (!otp[index] && index > 0) {
        // If current box is empty, clear previous box
        newOtp[index - 1] = "";
        setOtp(newOtp);
        otpRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        // If current box has value, clear it
        newOtp[index] = "";
        setOtp(newOtp);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      pastedData.split("").forEach((char, index) => {
        if (index < 6) newOtp[index] = char;
      });
      setOtp(newOtp);

      // Focus on last filled box
      const lastIndex = Math.min(pastedData.length - 1, 5);
      otpRefs.current[lastIndex]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setError("Please enter 6-digit OTP");
      return;
    }

    setLocalLoading(true);
    setError("");

    try {
      // Call parent component's verification function
      const result = await onVerifyOTP(otpValue);

      if (result && !result.success) {
        setError(result.message || "OTP verification failed");
        // Reset OTP on failure
        setOtp(["", "", "", "", "", ""]);
        otpRefs.current[0]?.focus();
      }
    } catch (error) {
      setError("OTP verification failed");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLocalLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || localLoading || isLoading) return;

    setLocalLoading(true);
    setError("");

    try {
      const result = await onResendOTP();

      if (result && result.success) {
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);

        // Focus on first input
        setTimeout(() => {
          otpRefs.current[0]?.focus();
        }, 100);
      } else {
        setError(result?.message || "Failed to resend OTP");
      }
    } catch (error) {
      setError("Failed to resend OTP");
    } finally {
      setLocalLoading(false);
    }
  };

  const handleClose = () => {
    resetState();
    toggle?.();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      backdrop="static"
      keyboard={false}
      size="md"
    >
      <ModalHeader toggle={handleClose} className="border-0 pb-0">
        <span className="visually-hidden">OTP Verification</span>
      </ModalHeader>
      <ModalBody className="p-4">
        <div className="text-center mb-4">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{
              width: "56px",
              height: "56px",
              background: "linear-gradient(135deg, #3b82f6 0%, #a855f7 100%)",
            }}
          >
            <Mail className="text-white" size={28} />
          </div>
          <h2 className="h4 fw-bold text-dark mb-2">Enter OTP</h2>
          <p className="text-muted small mb-1">
            We've sent a 6-digit code to your mobile
          </p>
          <p className="text-primary small fw-medium">{mobile}</p>
        </div>

        {/* Error Message */}
        {error && (
          <Alert color="danger" className="py-2 px-3 small text-center">
            {error}
          </Alert>
        )}

        {/* OTP Input Boxes - 6 separate boxes */}
        <div className="d-flex justify-content-center gap-2 mb-4">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (otpRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={localLoading || isLoading}
              className="form-control text-center fw-bold"
              style={{
                width: "48px",
                height: "56px",
                fontSize: "1.5rem",
                borderWidth: "2px",
                borderColor: digit ? "#3b82f6" : "#dee2e6",
                backgroundColor: digit ? "#eff6ff" : "#ffffff",
                transition: "all 0.2s",
              }}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {/* Verify OTP Button - Only shows when all 6 digits are entered */}
        {otp.join("").length === 6 && (
          <Button
            color="primary"
            className="w-100 d-flex align-items-center justify-content-center gap-2 mb-3"
            onClick={handleVerifyOTP}
            disabled={localLoading || isLoading}
            style={{
              background: "linear-gradient(90deg, #2563eb 0%, #9333ea 100%)",
              border: "none",
              padding: "12px",
            }}
          >
            {localLoading || isLoading ? (
              <Spinner size="sm" color="light" />
            ) : (
              <>
                Verify OTP
                <ArrowRight size={16} />
              </>
            )}
          </Button>
        )}

        {/* Resend OTP */}
        <div className="text-center mb-3">
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={!canResend || localLoading || isLoading}
            className="btn btn-link text-decoration-none small fw-medium p-0"
            style={{
              color: canResend ? "#2563eb" : "#6c757d",
              cursor: canResend ? "pointer" : "not-allowed",
            }}
          >
            {canResend ? "Resend OTP" : `Resend in ${timer}s`}
          </button>
        </div>

        <p className="text-center text-muted small mb-0">
          Enter the 6-digit verification code sent to your mobile
        </p>

        {/* Loading Overlay */}
        {(localLoading || isLoading) && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              borderRadius: "0.5rem",
              zIndex: 1000,
            }}
          >
            <Spinner color="primary" />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default OTPVerityLogin;
